import { useCallback, useLayoutEffect, useState } from 'react';

import {
  useCreateOperation as useCreateOperationApi,
  useOperationGetOrderByIdLazy,
} from 'api/requests';
import { useServerErrorNotify } from 'hooks';
import { FormSubmit } from 'libs/form';
import { useTranslation } from 'libs/i18n';
import { ROUTES, RoutesId, useNavigate } from 'libs/navigation';
import { useUser } from 'libs/providers';
import { OperationFormType } from 'modules/operation/common/forms/operationForm';
import { getOperationSubmitData } from 'modules/operation/common/helpers';

import { getOperationInitialValue } from '../helpers';

export const useCreateOperation = () => {
  const { user } = useUser();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);

  const { searchParams, canGoBack, goBack, navigate } = useNavigate();

  const { getOperationOrder, operationOrder } = useOperationGetOrderByIdLazy();

  const {
    loading: submitLoading,
    createOperation,
    error,
  } = useCreateOperationApi();

  const canChangeClientAndOrder = !searchParams.get('orderId');

  const breadcrumbsTitles = {
    [RoutesId.createOperation]: t('operation.createOperation.title'),
  };

  const initialValues = getOperationInitialValue(operationOrder, user!, t);

  // GMT+5 (Алматы)
  // TODO: Feature request to get broker timezone offset from API
  const brokerTimezoneOffset = 18000;

  const onBack = useCallback(() => {
    if (canGoBack) {
      goBack();
    } else {
      navigate(ROUTES.operations.fullPath, {});
    }
  }, [canGoBack, goBack, navigate]);

  const onSubmit = useCallback<FormSubmit<OperationFormType>>(
    async (values, helpers) => {
      const submitData = getOperationSubmitData(values, brokerTimezoneOffset);
      if (await createOperation(submitData)) {
        helpers.resetForm();
        onBack();
      }
    },
    [createOperation, onBack]
  );

  useLayoutEffect(() => {
    const orderId = searchParams.get('orderId');

    if (orderId) {
      setLoading(true);
      getOperationOrder(orderId).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [getOperationOrder, searchParams]);

  useServerErrorNotify(error);

  return {
    initialValues,
    breadcrumbsTitles,
    submitLoading,
    brokerTimezoneOffset,
    canChangeClientAndOrder,
    loading,
    onSubmit,
    onBack,
  };
};
